/**
 * AWC (Awin Click) tracking utilities for affiliate attribution
 */

/* eslint-env browser */

const AWC_STORAGE_KEY = 'awin_awc';
const AWC_EXPIRY_DAYS = 365; // Awin recommended attribution window

/**
 * Validates AWC format - basic validation for Awin AWC parameter
 */
export const isValidAwc = awc => {
  if (!awc || typeof awc !== 'string') return false;
  // Basic validation - AWC should be non-empty string
  // Awin AWCs typically contain underscores and alphanumeric characters
  return awc.length > 0 && /^[a-zA-Z0-9_-]+$/.test(awc);
};

/**
 * Stores AWC value in localStorage with expiration timestamp
 */
export const storeAwc = awc => {
  if (!isValidAwc(awc)) {
    console.warn('AWC: Invalid AWC format, not storing:', awc);
    return false;
  }

  const awcData = {
    awc,
    timestamp: Date.now(),
    expires: Date.now() + AWC_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
  };

  try {
    localStorage.setItem(AWC_STORAGE_KEY, JSON.stringify(awcData));
    console.log('AWC: Stored successfully:', awc);
    return true;
  } catch (error) {
    console.warn('AWC: Failed to store in localStorage, trying sessionStorage:', error);
    try {
      sessionStorage.setItem(AWC_STORAGE_KEY, JSON.stringify(awcData));
      console.log('AWC: Stored in sessionStorage:', awc);
      return true;
    } catch (sessionError) {
      console.error('AWC: Failed to store in both localStorage and sessionStorage:', sessionError);
      return false;
    }
  }
};

/**
 * Retrieves AWC value from storage if it hasn't expired
 */
export const getAwc = () => {
  try {
    // Try localStorage first
    let awcDataString = localStorage.getItem(AWC_STORAGE_KEY);

    // Fallback to sessionStorage
    if (!awcDataString) {
      awcDataString = sessionStorage.getItem(AWC_STORAGE_KEY);
    }

    if (!awcDataString) {
      return null;
    }

    const awcData = JSON.parse(awcDataString);

    // Check if expired
    if (Date.now() > awcData.expires) {
      console.log('AWC: Stored AWC has expired, removing');
      clearAwc();
      return null;
    }

    console.log('AWC: Retrieved valid AWC:', awcData.awc);
    return awcData.awc;
  } catch (error) {
    console.error('AWC: Error retrieving AWC from storage:', error);
    clearAwc(); // Clear corrupted data
    return null;
  }
};

/**
 * Clears AWC from both localStorage and sessionStorage
 */
export const clearAwc = () => {
  try {
    localStorage.removeItem(AWC_STORAGE_KEY);
    sessionStorage.removeItem(AWC_STORAGE_KEY);
    console.log('AWC: Cleared from storage');
  } catch (error) {
    console.error('AWC: Error clearing AWC from storage:', error);
  }
};

export const captureAwcFromUrl = () => {
  // implemented for easier testing purposes
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const awc = urlParams.get('awc');

    if (awc) {
      console.log('AWC: Found AWC parameter in URL:', awc);
      const stored = storeAwc(awc);

      if (stored) {
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('awc');
        window.history.replaceState({}, '', newUrl.toString());
        console.log('AWC: Cleaned URL after capturing AWC');
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('AWC: Error capturing AWC from URL:', error);
    return false;
  }
};

/**
 * Reads AWC value from the "awc" cookie
 * Returns the AWC value if found, null if not found or on error
 */
export const getAwcCookie = () => {
  try {
    // Check if document.cookie is available (browser environment)
    if (typeof document === 'undefined' || !document.cookie) {
      console.log('AWC: No cookies available');
      return null;
    }

    // Parse document.cookie to find the "awc" cookie
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();

      // Check if this cookie starts with "awc="
      if (cookie.indexOf('awc=') === 0) {
        const awcValue = cookie.substring(4); // Remove "awc=" prefix

        if (awcValue) {
          if (isValidAwc(awcValue)) {
            console.log('AWC: Found valid AWC in cookie:', awcValue);
            return awcValue;
          } else {
            console.warn('AWC: Invalid AWC format in cookie, ignoring:', awcValue);
            return null;
          }
        }
      }
    }

    console.log('AWC: AWC cookie not found');
    return null;
  } catch (error) {
    console.error('AWC: Error reading AWC from cookie:', error);
    return null;
  }
};

/**
 * Captures AWC from cookie or URL parameters and stores it
 * First tries cookie, then falls back to URL parameters
 * Returns true if AWC was captured and stored
 */
export const captureAwc = () => {
  try {
    const awc = getAwcCookie();
    if (awc) {
      console.log('AWC: Found AWC parameter in cookie:', awc);
      const stored = storeAwc(awc);

      if (stored) {
        console.log('AWC: Successfully captured and stored AWC from cookie');
        return true;
      }
    }

    const urlCaptured = captureAwcFromUrl();
    if (urlCaptured) {
      console.log('AWC: Successfully captured AWC from URL as fallback');
      return true;
    }

    console.log('AWC: No AWC found in cookie or URL parameters');
    return false;
  } catch (error) {
    console.error('AWC: Error capturing AWC from cookie or URL:', error);
    return false;
  }
};

/**
 * Gets AWC info for debugging/analytics
 */
export const getAwcInfo = () => {
  try {
    const awcDataString =
      localStorage.getItem(AWC_STORAGE_KEY) || sessionStorage.getItem(AWC_STORAGE_KEY);

    if (!awcDataString) {
      return { hasAwc: false };
    }

    const awcData = JSON.parse(awcDataString);
    const isExpired = Date.now() > awcData.expires;

    return {
      hasAwc: true,
      awc: awcData.awc,
      timestamp: awcData.timestamp,
      expires: awcData.expires,
      isExpired,
      daysRemaining: Math.max(
        0,
        Math.floor((awcData.expires - Date.now()) / (24 * 60 * 60 * 1000))
      ),
    };
  } catch (error) {
    console.error('AWC: Error getting AWC info:', error);
    return { hasAwc: false, error: error.message };
  }
};
